// footer/Footer.tsx
import React, { useCallback, useMemo, useState } from 'react';
import type { DropdownProps } from 'antd';
import type { AgentOption, SkillItem, SkillListQuery, SkillListResult } from '../types';
import { AgentDropdown } from './AgentDropdown';
import { SkillButton } from './SkillButton';
import type { SkillSelection, SkillCardActionType, SkillHeaderActionType } from './SkillPanel';
import { UploadButton } from './UploadButton';
import { AIImageButton } from './AIImageButton';
import { SendButton } from './SendButton';
import { PointsBadge } from './PointsBadge';
import { ToolbarOverflow, type ToolItem } from './ToolbarOverflow';
import { SkillIcon } from './SkillButton';

// 智能体在「更多」菜单里时的二级菜单对齐：弹层左上角(tl)贴触发行右上角(tr)、右移 4px，
// 即从菜单右侧级联展开。points 为 [弹层点, 目标点]（rc-trigger 约定）。
const AGENT_MENU_ALIGN_RIGHT: DropdownProps['align'] = { points: ['tl', 'tr'], offset: [4, 0] };

type Props = {
  isProcessing?: boolean;
  loading?: boolean;
  disabled?: boolean;
  /** 发送按钮专属禁用（对齐 V1 !isFormValid）：表单未填全/普通对话空文本时禁用，
   *  仅作用于发送按钮，不影响上传/agent 按钮。 */
  sendDisabled?: boolean;
  showFooterUpload?: boolean;
  footerFiles: File[];
  onFooterFilesChange: (files: File[]) => void;
  /** 选择资产库文件回调 */
  onAssetLibraryClick?: () => void;
  agentOptions?: AgentOption[];
  onAgentSelect?: (agent: AgentOption) => void;
  /** 智能体下拉展开状态变化（用于外部在打开瞬间聚焦输入框，触发选中态） */
  onAgentDropdownOpenChange?: (open: boolean) => void;
  hideAgentButton?: boolean;
  /** 当前选中智能体（由 index.tsx 从 config tag 反查 agentOptions 派生）→ 渲染积分徽标 */
  currentAgent?: AgentOption;
  /** 当前选中技能（null=未选）→ 技能按钮高亮+显示技能名；与智能体互斥由 index.tsx 协调 */
  selectedSkill?: SkillSelection | null;
  onSkillSelect?: (skill: SkillSelection | null) => void;
  /** 单独点击技能卡片主体回调（不回填、不改选中态，仅把技能信息抛给外部） */
  onSkillCardClick?: (skill: SkillSelection) => void;
  /** 技能卡片右上角图标点击回调（向输入框外暴露，action 标识图标类型） */
  onSkillAction?: (action: SkillCardActionType, skill: SkillSelection) => void | string | Promise<void | string>;
  /** 技能列表数据源：面板每次打开调一次刷新；删除成功后也刷新 */
  skillListFetch?: (query: SkillListQuery) => Promise<SkillItem[] | SkillListResult>;
  /** 头部「导入技能」/「基于会话创建技能」按钮点击回调，action 区分是哪个按钮 */
  onSkillHeaderAction?: (action: SkillHeaderActionType) => void;
  /** 是否显示「基于会话创建技能」按钮（智能体激活时隐藏）。默认显示 */
  showCreateSkill?: boolean;
  /** 「基于当前会话创建技能」按钮禁用状态 */
  createDisabled?: boolean;
  /** 受控：外部控制技能面板展开/收起 */
  skillPanelOpen?: boolean;
  /** 技能面板状态变化时通知外部 */
  onSkillPanelOpenChange?: (open: boolean) => void;
  /** 受控模式下面板打开的延迟时间（ms）。用于关闭弹窗后延迟打开面板，避免视觉闪烁 */
  skillPanelOpenDelay?: number;
  /** 技能面板最大高度，支持 px 数值或 CSS 高度值（默认 360px）；小屏时会按可用空间继续收缩 */
  skillPanelHeight?: string | number;
  /** AI生图按钮点击回调（仅暴露事件，业务逻辑外部接） */
  onAIImageClick?: () => void;
  /** AI生图按钮选中高亮态（外部受控；与智能体/技能不互斥） */
  aiImageSelected?: boolean;
  /** 智能体选中态点击 × 退出（与编辑器内智能体 tag 关闭同一路径）。 */
  onAgentExit?: () => void;
  /** AI生图选中态点击 × 退出（与编辑器内 AI生图 tag 关闭同一路径）。 */
  onAIImageExit?: () => void;
  onSend: () => void;
  onPause?: () => void;
  /** 智能体模式传 false，loading 时继续展示不可点击的加载态。 */
  canPause?: boolean;
  padding?: string;
};

export const Footer: React.FC<Props> = React.memo(
  ({
    isProcessing,
    loading,
    disabled,
    sendDisabled,
    showFooterUpload = true,
    footerFiles,
    onFooterFilesChange,
    onAssetLibraryClick,
    agentOptions,
    onAgentSelect,
    onAgentDropdownOpenChange,
    hideAgentButton,
    currentAgent,
    selectedSkill,
    onSkillSelect,
    onSkillCardClick,
    onSkillAction,
    skillListFetch,
    onSkillHeaderAction,
    showCreateSkill,
    createDisabled,
    skillPanelOpen,
    onSkillPanelOpenChange,
    skillPanelOpenDelay,
    skillPanelHeight,
    onAIImageClick,
    aiImageSelected,
    onAgentExit,
    onAIImageExit,
    onSend,
    onPause,
    canPause = false,
    padding = '12px 16px',
  }) => {
    const handleUpload = useCallback(
      (files: File[]) => {
        const max = 50 * 1024 * 1024;
        const total = files.reduce((s, f) => s + f.size, 0);
        if (total > max) {
          const accepted: File[] = [];
          let sum = 0;
          for (const f of files) {
            if (sum + f.size > max) break;
            accepted.push(f);
            sum += f.size;
          }
          onFooterFilesChange(accepted);
        } else {
          onFooterFilesChange(files);
        }
      },
      [onFooterFilesChange],
    );

    // 本地跟踪技能面板开合：用于「面板打开时就把技能按钮从『更多』里择出来」（对齐千问：
    // 点开工具即拉出，不必等选中）。非受控时以此为准；受控时以外部 skillPanelOpen 为准。
    const [skillOpenInternal, setSkillOpenInternal] = useState(false);
    const skillPanelIsOpen = skillPanelOpen ?? skillOpenInternal;
    const handleSkillPanelOpenChange = useCallback(
      (open: boolean) => {
        setSkillOpenInternal(open);
        onSkillPanelOpenChange?.(open);
      },
      [onSkillPanelOpenChange],
    );

    // 功能按钮集合（顺序即平铺顺序：从左到右）。空间不足时由 ToolbarOverflow 从右往左
    // 收进「更多」——即 AI生图 先折叠，其次 技能，最后 智能体（与千问一致）。
    const showAgent = !hideAgentButton && !!agentOptions && agentOptions.length > 0;
    const toolItems = useMemo<ToolItem[]>(() => {
      const list: ToolItem[] = [];
      // node 写成函数：拿到 close 以便「选完就关」——被折叠进「更多」时，真正选中后收起菜单；
      // 内联时 close 是无害 no-op（菜单本就没开）。
      if (showAgent) {
        list.push({
          key: 'agent',
          // 选中智能体后置顶常驻、不折叠进「更多」（与技能一致）。智能体与技能互斥，不会同时置顶。
          pinned: !!currentAgent,
          node: ({ close, inMenu }) => (
            <AgentDropdown
              options={agentOptions!}
              onSelect={(a) => {
                onAgentSelect?.(a);
                close();
              }}
              disabled={disabled || isProcessing}
              currentAgent={currentAgent}
              onOpenChange={onAgentDropdownOpenChange}
              onExit={onAgentExit}
              // 在「更多」菜单里时二级菜单从右侧级联展开（弹层左上贴触发行右上，右移 4px），
              // 不遮住「更多」列表；内联时传 undefined 走默认 bottomLeft 向下展开
              align={inMenu ? AGENT_MENU_ALIGN_RIGHT : undefined}
            />
          ),
        });
      }
      list.push({
        key: 'skill',
        // 置顶常驻、不折叠进「更多」（对齐千问）：① 选中技能后（显示成带 × 的 chip）；
        // ② 技能面板打开时（浏览技能过程中就把按钮择出来，而非等选中）。
        pinned: !!selectedSkill || skillPanelIsOpen,
        // 技能面板由 SkillButton 自身渲染、锚定编辑器，故常驻内联（溢出时隐藏但保持挂载），
        // 不随「更多」菜单卸载。
        node: (
          <SkillButton
            disabled={disabled || isProcessing}
            selectedSkill={selectedSkill}
            onSkillSelect={onSkillSelect}
            onSkillCardClick={onSkillCardClick}
            onSkillAction={onSkillAction}
            skillListFetch={skillListFetch}
            onSkillHeaderAction={onSkillHeaderAction}
            showCreateSkill={showCreateSkill}
            createDisabled={createDisabled}
            skillPanelOpen={skillPanelOpen}
            onSkillPanelOpenChange={handleSkillPanelOpenChange}
            skillPanelOpenDelay={skillPanelOpenDelay}
            skillPanelHeight={skillPanelHeight}
          />
        ),
        // 「更多」里的技能代理行：点击 → 触发内联的真实技能按钮打开面板 + 收起菜单
        renderMenuItem: ({ close, activate }) => (
          <div
            className={`rte-skill-badge${selectedSkill ? ' rte-skill-badge-selected' : ''}`}
            data-rte-skill-button="true"
            onClick={() => {
              if (disabled || isProcessing) return;
              activate();
              close();
            }}
            style={{ cursor: disabled || isProcessing ? 'not-allowed' : 'pointer' }}
          >
            <SkillIcon />
            <span className="rte-skill-text">{selectedSkill?.title || '技能'}</span>
          </div>
        ),
      });
      list.push({
        key: 'ai-image',
        // 选中 AI生图 后置顶常驻、不折叠进「更多」（与技能/智能体一致）。
        pinned: !!aiImageSelected,
        node: ({ close }) => (
          <AIImageButton
            selected={aiImageSelected}
            onClick={() => {
              onAIImageClick?.();
              close();
            }}
            onExit={onAIImageExit}
            disabled={disabled || isProcessing}
          />
        ),
      });
      return list;
    }, [
      showAgent,
      agentOptions,
      onAgentSelect,
      disabled,
      isProcessing,
      currentAgent,
      onAgentDropdownOpenChange,
      selectedSkill,
      onSkillSelect,
      onSkillCardClick,
      onSkillAction,
      skillListFetch,
      onSkillHeaderAction,
      showCreateSkill,
      createDisabled,
      skillPanelOpen,
      skillPanelIsOpen,
      handleSkillPanelOpenChange,
      skillPanelOpenDelay,
      skillPanelHeight,
      aiImageSelected,
      onAIImageClick,
      onAgentExit,
      onAIImageExit,
    ]);

    // 会改变按钮宽度的因素（技能名长度 / 智能体积分徽标 / 各选中态）变化时，触发重新测量
    const measureKey = `${showAgent ? '1' : '0'}|${selectedSkill?.title ?? ''}|${currentAgent?.key ?? ''}|${aiImageSelected ? '1' : '0'}`;

    return (
      <div className="rich-text-editor-footer" style={{ padding }}>
        <div className="rich-text-editor-footer-left">
          {/* 上传按钮移到最左（对齐 figma 2805:30796）；与竖分隔线同受 showFooterUpload 控制 */}
          {showFooterUpload && (
            <UploadButton
              files={footerFiles}
              onChange={handleUpload}
              disabled={disabled || isProcessing}
              onAssetLibraryClick={onAssetLibraryClick}
            />
          )}
          {showFooterUpload && <div className="rte-footer-divider" />}
          <ToolbarOverflow items={toolItems} measureKey={measureKey} disabled={disabled || isProcessing} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          {/* <div className="rte-brand-badge">
            <div className="rte-brand-badge">
              <img src="https://oospublic.sealseek.cn/file/website/ball.png" alt="" />
            </div>
            <span className="rte-brand-text" style={{ marginLeft: '4px' }}>
              SealSeek 2.0
            </span>
          </div> */}
          <PointsBadge currentAgent={currentAgent} />
          <SendButton
            onClick={onSend}
            onPause={onPause}
            loading={loading || isProcessing}
            pausable={Boolean(loading && canPause && onPause)}
            disabled={disabled || (sendDisabled && !(loading && canPause && onPause))}
          />
        </div>
      </div>
    );
  },
);
Footer.displayName = 'Footer';
